import React, { useEffect, useRef, useState } from 'react'
import MonacoEditor, { Monaco, loader } from '@monaco-editor/react'
import { Space, Table, Tabs } from 'antd'
import styles from './index.css'
import { StopOutlined } from '@ant-design/icons'
import { getFormatMessage, mergeTableColumns } from '@/utils/utils'
import { TemplateMacroEditorProps } from '@/template/interface'
import { getCompletionItems } from '../docs/util'
import * as monaco from 'monaco-editor'

loader.config({ monaco })

const { TabPane } = Tabs

export const TemplateMacroEditor: React.FC<TemplateMacroEditorProps> = (
  props: TemplateMacroEditorProps
) => {
  const [activePanel, setActivePanel] = useState<string>('output')
  const [breakPoints] = useState<Map<number, string>>(new Map())
  const formatMessage = getFormatMessage()
  const editorRef = useRef<any>(null)
  const options = {
    minimap: {
      enabled: false
    },
    lineNumbersMinChars: 4,
    glyphMargin: true
  }

  const defaultColumns = [
    {
      title: formatMessage({ id: 'app.template.macro.editor.variable' }),
      key: 'app.template.macro.editor.variable',
      dataIndex: 'name'
    },
    {
      title: formatMessage({ id: 'app.template.macro.editor.offset' }),
      key: 'app.template.macro.editor.offset',
      dataIndex: 'offset'
    },
    {
      title: formatMessage({ id: 'app.template.macro.editor.value' }),
      key: 'app.template.macro.editor.value',
      dataIndex: 'value'
    }
  ]
  const columns = mergeTableColumns(defaultColumns, props.watchColumns)
  let completionDisposable

  const handleBeforeMount = (monaco: Monaco) => {
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      noLib: true,
      allowNonTsExtensions: true
    })
    completionDisposable = monaco.languages.registerCompletionItemProvider(
      'javascript',
      {
        triggerCharacters: ['@'],
        provideCompletionItems: () => {
          return {
            suggestions: getCompletionItems(0)
          } as any
        }
      }
    )
  }

  const handleMount = (editor, monaco: Monaco) => {
    editorRef.current = editor
    editor.onMouseDown((e) => {
      if (e.target.type === 2) {
        // add/remove break point
        const row = e.target.range.startLineNumber
        const content = editor.getModel().getLineContent(row) as string
        // 代码为空不能添加断点
        if (content.trim() === '') {
          return
        }
        if (breakPoints.has(row)) {
          const id = breakPoints.get(row)
          editor.deltaDecorations([id], [])
          breakPoints.delete(row)
        } else {
          const decorations = editor.deltaDecorations(
            [],
            [
              {
                range: new monaco.Range(row, 1, row, 1),
                options: {
                  isWholeLine: false,
                  glyphMarginClassName: 'monaco-breakpoint'
                }
              }
            ]
          )
          breakPoints.set(row, decorations[0])
        }
        props.onBreakPointsChange?.(breakPoints)
      }
    })
  }

  useEffect(() => {
    if (props.selection) {
      editorRef.current?.setSelection({
        startLineNumber: props.selection.start.row,
        startColumn: props.selection.start.column,
        endLineNumber: props.selection.end.row,
        endColumn: props.selection.end.column
      })
    }
  }, [props.selection])

  useEffect(() => {
    return () => {
      completionDisposable?.dispose()
      editorRef.current?.dispose()
    }
  }, [])

  return (
    <div>
      <div className={styles['editor']}>
        <MonacoEditor
          options={options}
          defaultLanguage='javascript'
          defaultValue={props.defaultValue}
          beforeMount={handleBeforeMount}
          onMount={handleMount}
          onChange={(value, ev) => props.onChange?.(value || '')}
        />
      </div>
      <Tabs
        type='card'
        size='small'
        className={styles['console']}
        tabBarStyle={{
          paddingRight: 10,
          borderRight: '1px solid #f0f0f0',
          marginBottom: 0
        }}
        activeKey={activePanel}
        onChange={setActivePanel}
        tabBarExtraContent={
          <Space>
            {activePanel == 'output' && (
              <StopOutlined
                style={{ cursor: 'pointer' }}
                onClick={() => props.onClear?.()}
              />
            )}
          </Space>
        }
      >
        <TabPane
          tab={formatMessage({ id: 'app.template.macro.editor.output' })}
          key='output'
          className={styles['panel']}
        >
          <textarea readOnly={true} value={props.output} />
        </TabPane>

        <TabPane
          tab={formatMessage({ id: 'app.template.macro.editor.watch' })}
          key='watch'
          className={styles['panel']}
        >
          <Table
            rowKey='name'
            columns={columns}
            dataSource={props.watchItems}
            size='small'
            scroll={{ y: 160 }}
            pagination={false}
          />
        </TabPane>
      </Tabs>
    </div>
  )
}
